// ===== Interfaces =====

import {
    ITranscodeAudioForUploadData,
    ITranscodeAudioForUploadResult,
} from '../interfaces';

const AUDIO_UPLOAD_MIME_TYPE_ALIASES: Record<string, string> = Object.freeze({
    'application/ogg': 'audio/ogg',
    'application/x-ogg': 'audio/ogg',
    'audio/mp3': 'audio/mpeg',
    'audio/mpeg3': 'audio/mpeg',
    'audio/x-flac': 'audio/flac',
    'audio/x-mp3': 'audio/mpeg',
    'audio/x-mpeg': 'audio/mpeg',
    'audio/x-mpeg3': 'audio/mpeg',
});

// Browser-side transcode budget: above this, we skip client ffmpeg and let backend handle it.
const DEFAULT_BROWSER_TRANSCODE_MAX_BYTES = 30 * 1024 * 1024;

const getBrowserTranscodeMaxBytes = (): number => {
    // Allows per-environment tuning without code changes (e.g. preview vs production).
    const parsed = Number.parseInt(
        process.env.REACT_APP_AUDIO_BROWSER_TRANSCODE_MAX_BYTES || `${DEFAULT_BROWSER_TRANSCODE_MAX_BYTES}`,
        10,
    );
    if (Number.isNaN(parsed) || parsed < 1) return DEFAULT_BROWSER_TRANSCODE_MAX_BYTES;
    return parsed;
};

let ffmpegInstance: any = null;
let ffmpegLoaderPromise: Promise<any> | null = null;

/**
 * Keeps upload metadata on canonical `audio/*` MIME types so the backend audio
 * trigger sees imported files even when the browser reports legacy aliases.
 */
export const resolveAudioUploadMimeType = (mimeType: string): string => {
    const normalizedMimeType = (mimeType || '').trim().toLowerCase();
    if (!normalizedMimeType) {
        return mimeType;
    }

    return AUDIO_UPLOAD_MIME_TYPE_ALIASES[normalizedMimeType] || normalizedMimeType;
};

/**
 * Browser ffmpeg is now opt-in only. The backend audio pipeline already
 * handles normalization/transcoding, and keeping imports on that path avoids
 * heavy in-tab WASM work that can destabilize Chromium during regular uploads.
 */
export const shouldUseBrowserAudioTranscode = (data: {
    blobSize: number,
    mimeType: string,
    forceTranscode?: boolean,
}): boolean => {
    const {
        blobSize,
        mimeType,
        forceTranscode = false,
    } = data;
    const normalizedMimeType = resolveAudioUploadMimeType(mimeType);

    if (!forceTranscode) {
        return false;
    }

    if (!normalizedMimeType) {
        return false;
    }

    return blobSize <= getBrowserTranscodeMaxBytes();
};

// Keep extensions explicit so ffmpeg gets a decodable filename.
const getAudioFileExtensionFromMimeType = (mimeType: string): string => {
    const normalized = (mimeType || '').toLowerCase();
    if (normalized.includes('wav')) return 'wav';
    if (normalized.includes('ogg')) return 'ogg';
    if (normalized.includes('webm')) return 'webm';
    if (normalized.includes('aac')) return 'aac';
    if (normalized.includes('flac')) return 'flac';
    if (normalized.includes('mp3') || normalized.includes('mpeg')) return 'mp3';
    return 'bin';
};

const getFFmpeg = async (): Promise<any> => {
    if (ffmpegInstance) return ffmpegInstance;
    if (ffmpegLoaderPromise) return ffmpegLoaderPromise;

    // Keep one shared loader/promise to avoid reloading ffmpeg core on repeated uploads.
    ffmpegLoaderPromise = (async () => {
        // eslint-disable-next-line import/no-extraneous-dependencies
        const ffmpegModule = await import('@ffmpeg/ffmpeg');
        // eslint-disable-next-line import/no-extraneous-dependencies
        const ffmpegUtilModule = await import('@ffmpeg/util');
        const { FFmpeg } = ffmpegModule;
        const { toBlobURL } = ffmpegUtilModule;

        // Use CDN-hosted core binaries; app bundle only includes JS wrapper.
        const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
        const coreURL = await toBlobURL(
            `${baseURL}/ffmpeg-core.js`,
            'text/javascript',
        );
        const wasmURL = await toBlobURL(
            `${baseURL}/ffmpeg-core.wasm`,
            'application/wasm',
        );

        const ffmpeg = new FFmpeg();
        await ffmpeg.load({
            coreURL,
            wasmURL,
        });
        ffmpegInstance = ffmpeg;
        return ffmpeg;
    })();

    return ffmpegLoaderPromise;
};

const transcodeAudioForUpload = async (data: ITranscodeAudioForUploadData): Promise<ITranscodeAudioForUploadResult> => {
    const {
        blob,
        mimeType,
        onProgress,
        forceTranscode = false,
    } = data;
    const normalizedMimeType = resolveAudioUploadMimeType(mimeType);

    if (
        typeof window === 'undefined'
        || !shouldUseBrowserAudioTranscode({
            blobSize: blob.size,
            forceTranscode,
            mimeType: normalizedMimeType,
        })
    ) {
        if (onProgress) onProgress(100);
        return {
            blob,
            mimeType: normalizedMimeType,
            transcoded: false,
        };
    }

    if (onProgress) onProgress(5);
    const ffmpeg = await getFFmpeg();
    if (onProgress) onProgress(15);
    const inputExtension = getAudioFileExtensionFromMimeType(normalizedMimeType);
    const inputFileName = `input.${inputExtension}`;
    const outputFileName = 'output.mp3';
    const inputData = new Uint8Array(await blob.arrayBuffer());

    await ffmpeg.writeFile(inputFileName, inputData);
    if (onProgress) onProgress(30);
    // Keep output quality aligned with backend transcode defaults.
    await ffmpeg.exec([
        '-i',
        inputFileName,
        '-b:a',
        '320k',
        outputFileName,
    ]);
    if (onProgress) onProgress(85);
    const outputData = await ffmpeg.readFile(outputFileName);
    await ffmpeg.deleteFile(inputFileName);
    await ffmpeg.deleteFile(outputFileName);
    if (onProgress) onProgress(100);

    const outputBlob = new Blob(
        [outputData],
        { type: 'audio/mpeg' },
    );
    return {
        blob: outputBlob,
        mimeType: 'audio/mpeg',
        transcoded: true,
    };
};

export default transcodeAudioForUpload;
